import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const PHP_API = process.env.REACT_APP_PHP_API_URL + "zuribuildAPI/";

const CURRENCIES = [
  { code: "FCFA", label: "FCFA — Franc CFA Afrique de l'Ouest" },
  { code: "XAF", label: "XAF — Franc CFA Afrique Centrale" },
  { code: "NGN", label: "NGN — Naira Nigérian" },
  { code: "GHS", label: "GHS — Cedi Ghanéen" },
  { code: "KES", label: "KES — Shilling Kényan" },
  { code: "ZAR", label: "ZAR — Rand Sud-Africain" },
  { code: "EGP", label: "EGP — Livre Égyptienne" },
  { code: "MAD", label: "MAD — Dirham Marocain" },
  { code: "TZS", label: "TZS — Shilling Tanzanien" },
  { code: "UGX", label: "UGX — Shilling Ougandais" },
  { code: "USD", label: "USD — Dollar Américain" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — Livre Sterling" },
  { code: "CAD", label: "CAD — Dollar Canadien" },
  { code: "XOF", label: "XOF — Franc CFA BCEAO" },
];

const EMPTY_PRODUCT = {
  name: "", price: "", currency: "FCFA", category: "",
  description: "", main_image: "", images: ["", "", "", "", ""], active: true,
};

export default function Store() {
  const { folderName } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const firebaseUser = JSON.parse(localStorage.getItem('firebase_user') || '{}');
  const memberId = firebaseUser?.uid || '';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('user_info') || !memberId) {
      navigate('/login');
      return;
    }
    // Verify this folder belongs to the current user
    axios.post(PHP_API + "check_ownership.php", { folder_name: folderName, member_id: memberId })
      .then((res) => {
        if (!res.data.success) navigate('/dashboard');
      })
      .catch(() => navigate('/dashboard'));
  }, []);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [media, setMedia] = useState([]);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [activeImageField, setActiveImageField] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatInput, setNewCatInput] = useState("");
  const [editingCatIndex, setEditingCatIndex] = useState(null);
  const [editingCatValue, setEditingCatValue] = useState("");
  const catInputRef = useRef(null);

  useEffect(() => {
    loadProducts();
    loadMedia();
    loadCategories();
  }, [folderName]);

  const loadProducts = () => {
    setLoading(true);
    axios.post(PHP_API + "get_products.php", { folder_name: folderName, member_id: memberId })
      .then((res) => { if (res.data.success) setProducts(res.data.products || []); })
      .catch(() => toast.error(t("store.failed_to_load")))
      .finally(() => setLoading(false));
  };

  const loadMedia = () => {
    axios.post(PHP_API + "get_media.php", { folder_name: folderName, member_id: memberId })
      .then((res) => { if (res.data.success) setMedia(res.data.media || []); })
      .catch(() => {});
  };

  const loadCategories = () => {
    axios.post(PHP_API + "get_categories.php", { folder_name: folderName, member_id: memberId })
      .then((res) => { if (res.data.success) setCategories(res.data.categories || []); })
      .catch(() => {});
  };

  const saveCategories = async (updated) => {
    try {
      const res = await axios.post(PHP_API + "save_categories.php", { folder_name: folderName, categories: updated, member_id: memberId });
      if (res.data.success) { setCategories(res.data.categories); return true; }
    } catch {}
    toast.error(t("store.failed_to_save"));
    return false;
  };

  const handleAddCategory = async () => {
    const val = newCatInput.trim();
    if (!val) return;
    if (categories.includes(val)) { toast.error(t("store.failed_to_save")); return; }
    const ok = await saveCategories([...categories, val]);
    if (ok) { setNewCatInput(""); setShowAddCat(false); }
  };

  const handleDeleteCategory = async (index) => {
    if (!window.confirm(t("store.delete_confirm"))) return;
    const updated = categories.filter((_, i) => i !== index);
    await saveCategories(updated);
  };

  const handleEditCategory = (index) => { setEditingCatIndex(index); setEditingCatValue(categories[index]); };

  const handleSaveEditCategory = async () => {
    const val = editingCatValue.trim();
    if (!val) return;
    const updated = [...categories];
    updated[editingCatIndex] = val;
    const ok = await saveCategories(updated);
    if (ok) { setEditingCatIndex(null); setEditingCatValue(""); }
  };

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateImage = (index, value) => {
    setForm((prev) => { const images = [...prev.images]; images[index] = value; return { ...prev, images }; });
  };

  const openAdd = () => { setEditingProduct(null); setForm(EMPTY_PRODUCT); setShowForm(true); };

  const openEdit = (product) => {
    setEditingProduct(product.id);
    setForm({
      name: product.name || "", price: product.price || "", currency: product.currency || "FCFA",
      category: product.category || "", description: product.description || "",
      main_image: product.main_image || "",
      images: product.images?.length ? [...product.images, ...Array(5).fill("")].slice(0, 5) : ["", "", "", "", ""],
      active: product.active !== false,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t("store.product_name_required")); return; }
    if (!form.price.trim()) { toast.error(t("store.price_required")); return; }
    setSaving(true);
    try {
      const endpoint = editingProduct ? "update_product.php" : "add_product.php";
      const payload = {
        folder_name: folderName,
        member_id: memberId,
        product: { ...form, images: form.images.filter((img) => img.trim() !== ""), ...(editingProduct ? { id: editingProduct } : {}) },
      };
      const res = await axios.post(PHP_API + endpoint, payload);
      if (res.data.success) {
        toast.success(editingProduct ? t("store.product_updated") : t("store.product_added"), { position: "bottom-right" });
        setShowForm(false);
        loadProducts();
      } else {
        toast.error(res.data.message || t("store.failed_to_save"));
      }
    } catch { toast.error(t("store.server_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(t("store.delete_confirm"))) return;
    try {
      const res = await axios.post(PHP_API + "delete_product.php", { folder_name: folderName, product_id: productId, member_id: memberId });
      if (res.data.success) { toast.success(t("store.product_deleted"), { position: "bottom-right" }); loadProducts(); }
    } catch { toast.error(t("store.delete_failed")); }
  };

  const openMediaPicker = (field) => { setActiveImageField(field); setShowMediaModal(true); };

  const selectImage = (url) => {
    if (activeImageField === "main_image") updateForm("main_image", url);
    else if (typeof activeImageField === "number") updateImage(activeImageField, url);
    setShowMediaModal(false);
    setActiveImageField(null);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t("store.max_size")); return; }
    const formData = new FormData();
    formData.append("image", file);
    formData.append("folder_name", folderName);
    setUploading(true);
    try {
      const res = await axios.post(PHP_API + "upload_image.php", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.success) { setMedia((prev) => [...prev, res.data.image]); toast.success(t("store.uploaded"), { position: "bottom-right" }); }
    } catch { toast.error(t("store.upload_failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const ImageInput = ({ label, value, onChange, onPick }) => (
    <div style={styles.fieldGroup}>
      <label style={styles.fieldLabel}>{label}</label>
      {value && <img src={value} alt="" style={styles.imgPreview} onError={(e) => { e.target.style.display = "none"; }} />}
      <div style={{ display: "flex", gap: 6 }}>
        <input style={{ ...styles.input, flex: 1 }} value={value || ""} placeholder={t("store.image_placeholder")} onChange={(e) => onChange(e.target.value)} />
        <button style={styles.btnPick} onClick={onPick} type="button"><i className="bi bi-images" /></button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`* { box-sizing: border-box; } body { margin: 0; font-family: 'Inter', -apple-system, sans-serif; background: #f5f5f7; }`}</style>

      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Link to="/dashboard" style={{ textDecoration: "none" }}>
            <img src="/zuribuild.png" style={{ width: "40px", marginTop: "-10px" }} alt="logo" />
            <span style={styles.logo}>ZuriBuild</span>
          </Link>
          <span style={styles.divider} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* ─── UPDATED: View Store link → global store-app ─── */}
		
		    <a href={process.env.REACT_APP_STORE_URL+`?folder=${folderName}`} target="_blank" rel="noreferrer" style={styles.btnPreview}>
		  {/*  <a href={`http://10.0.0.73/zuribuildAPI/store-app/?folder=${folderName}`} target="_blank" rel="noreferrer" style={styles.btnPreview}> */}
            <i className="bi bi-eye" /> {t("store.view_store")}
          </a>
          <Link to={`/edit-website/${folderName}`} style={styles.btnEdit}>
            <i className="bi bi-cross" /> {t("store.cancel")}
          </Link>
        </div>
      </div>

      <div style={styles.container}>

        {/* Categories */}
        <div style={styles.sectionBox}>
          <div style={styles.sectionHeader}>
            <div>
              <h6 style={{ margin: 0, fontWeight: 700 }}>{t("store.category")}</h6>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#aaa" }}>{t("store.all_categories")}</p>
            </div>
            <button style={styles.btnAddCat} onClick={() => { setShowAddCat(true); setTimeout(() => catInputRef.current?.focus(), 50); }}>
              <i className="bi bi-plus-lg" /> {t("store.category")}
            </button>
          </div>

          {showAddCat && (
            <div style={styles.catInputRow}>
              <input
                ref={catInputRef}
                style={{ ...styles.input, flex: 1 }}
                value={newCatInput}
                onChange={(e) => setNewCatInput(e.target.value)}
                placeholder={t("store.category_placeholder")}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); if (e.key === "Escape") { setShowAddCat(false); setNewCatInput(""); } }}
              />
              <button style={styles.btnConfirm} onClick={handleAddCategory}><i className="bi bi-check-lg" /></button>
              <button style={styles.btnCancelCat} onClick={() => { setShowAddCat(false); setNewCatInput(""); }}><i className="bi bi-x-lg" /></button>
            </div>
          )}

          <div style={styles.catChips}>
            {categories.length === 0 ? (
              <p style={{ color: "#bbb", fontSize: 13, margin: 0 }}>{t("store.no_results")}</p>
            ) : categories.map((cat, i) => (
              <div key={i} style={styles.catChip}>
                {editingCatIndex === i ? (
                  <div style={styles.catInputRow}>
                    <input style={{ ...styles.input, flex: 1, fontSize: 13, padding: "4px 8px" }} value={editingCatValue} onChange={(e) => setEditingCatValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSaveEditCategory(); if (e.key === "Escape") setEditingCatIndex(null); }} autoFocus />
                    <button style={styles.btnConfirm} onClick={handleSaveEditCategory}><i className="bi bi-check-lg" /></button>
                    <button style={styles.btnCancelCat} onClick={() => setEditingCatIndex(null)}><i className="bi bi-x-lg" /></button>
                  </div>
                ) : (
                  <>
                    <span style={styles.catChipLabel}>{cat}</span>
                    <button style={styles.catChipBtn} onClick={() => handleEditCategory(i)}><i className="bi bi-pencil" /></button>
                    <button style={{ ...styles.catChipBtn, color: "#e11d48" }} onClick={() => handleDeleteCategory(i)}><i className="bi bi-x-lg" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Products */}
        <div style={styles.sectionHeader}>
          <h6 style={{ margin: 0, fontWeight: 700 }}>{t("store.products")}</h6>
          <button style={styles.btnAdd} onClick={openAdd}>
            <i className="bi bi-plus-lg" /> {t("store.add_product")}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}><div className="spinner-border" style={{ color: "purple" }} /></div>
        ) : products.length === 0 ? (
          <div style={styles.emptyState}>
            <i className="bi bi-bag-x" style={{ fontSize: 48, color: "#ddd" }} />
            <p style={{ color: "#aaa" }}>{t("store.no_products")}</p>
            <button style={styles.btnAdd} onClick={openAdd}><i className="bi bi-plus-lg" /> {t("store.add_product")}</button>
          </div>
        ) : (
          <div style={styles.productsGrid}>
            {products.map((product) => (
              <div key={product.id} style={styles.productCard}>
                <div style={styles.productImageWrap}>
                  {product.main_image ? <img src={product.main_image} alt={product.name} style={styles.productImage} /> : <div style={styles.productImagePlaceholder}><i className="bi bi-image" style={{ fontSize: 32, color: "#ddd" }} /></div>}
                  <div style={{ ...styles.activeBadge, background: product.active ? "#22c55e" : "#aaa" }}>
                    {product.active ? t("store.active") : t("store.hidden")}
                  </div>
                  {product.category && <div style={styles.categoryBadge}>{product.category}</div>}
                </div>
                <div style={styles.productInfo}>
                  <p style={styles.productName}>{product.name}</p>
                  <p style={styles.productPrice}>{product.price} {product.currency}</p>
                  <p style={styles.productDesc}>{product.description?.substring(0, 80)}...</p>
                </div>
                <div style={styles.productActions}>
                  <button style={styles.btnEditProduct} onClick={() => openEdit(product)}><i className="bi bi-pencil" /> {t("store.edit")}</button>
                  {/* ─── UPDATED: View Product link → global store-app ─── */}
                  <a href={process.env.REACT_APP_STORE_URL+`product-detail.php?folder=${folderName}&slug=${product.slug}`} target="_blank" rel="noreferrer" style={styles.btnViewProduct}>
                    <i className="bi bi-eye" /> {t("store.view")}
                  </a>
                  <button style={styles.btnDeleteProduct} onClick={() => handleDelete(product.id)}><i className="bi bi-trash" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div style={styles.modalOverlay} onClick={() => setShowForm(false)}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h6 style={{ margin: 0, fontWeight: 600 }}>{editingProduct ? t("store.edit_product") : t("store.add_product")}</h6>
              <button style={styles.btnClose} onClick={() => setShowForm(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>{t("store.product_name")} *</label>
                <input style={styles.input} value={form.name} onChange={(e) => updateForm("name", e.target.value)} placeholder={t("store.product_name_placeholder")} />
              </div>
              <div style={styles.grid2}>
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>{t("store.price")} *</label>
                  <input style={styles.input} value={form.price} onChange={(e) => updateForm("price", e.target.value)} placeholder={t("store.price_placeholder")} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>{t("store.currency")}</label>
                  <select style={styles.input} value={form.currency} onChange={(e) => updateForm("currency", e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label.split("—")[1].trim()}</option>)}
                  </select>
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>{t("store.category")}</label>
                {categories.length === 0 ? (
                  <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
                    {t("store.no_results")} — <span style={{ color: "#7C3AED", cursor: "pointer" }} onClick={() => setShowForm(false)}>{t("store.add_product")}</span>
                  </p>
                ) : (
                  <select style={styles.input} value={form.category} onChange={(e) => updateForm("category", e.target.value)}>
                    <option value="">— {t("store.all_categories")} —</option>
                    {categories.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
                  </select>
                )}
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>{t("store.description")}</label>
                <textarea style={styles.textarea} value={form.description} onChange={(e) => updateForm("description", e.target.value)} rows={3} placeholder={t("store.description_placeholder")} />
              </div>
              <ImageInput label={t("store.main_image")} value={form.main_image} onChange={(v) => updateForm("main_image", v)} onPick={() => openMediaPicker("main_image")} />
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>{t("store.additional_images")}</label>
                {form.images.map((img, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <ImageInput label={`${t("store.view")} ${i + 1}`} value={img} onChange={(v) => updateImage(i, v)} onPick={() => openMediaPicker(i)} />
                  </div>
                ))}
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>{t("store.status")}</label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ ...styles.toggleBig, background: form.active ? "#7C3AED" : "#ccc" }} onClick={() => updateForm("active", !form.active)}>
                    <div style={{ ...styles.toggleDotBig, left: form.active ? 22 : 2 }} />
                  </div>
                  <span style={{ fontSize: 13, color: "#555" }}>{form.active ? t("store.active_label") : t("store.hidden_label")}</span>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnCancel} onClick={() => setShowForm(false)}>{t("store.cancel")}</button>
              <button style={{ ...styles.btnSave, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm" /> {t("store.saving")}</> : <><i className="bi bi-floppy" /> {t("store.save_product")}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      {showMediaModal && (
        <div style={styles.modalOverlay} onClick={() => setShowMediaModal(false)}>
          <div style={{ ...styles.modalBox, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h6 style={{ margin: 0, fontWeight: 600 }}>{t("edit-website.pick_image")}</h6>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button style={styles.btnUpload} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? t("store.uploading") : <><i className="bi bi-cloud-upload" /> {t("store.upload")}</>}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleUpload} />
                <button style={styles.btnClose} onClick={() => setShowMediaModal(false)}><i className="bi bi-x-lg" /></button>
              </div>
            </div>
            <div style={styles.modalBody}>
              {media.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <i className="bi bi-image" style={{ fontSize: 36, color: "#ddd" }} />
                  <p style={{ color: "#aaa", marginTop: 8, fontSize: 13 }}>{t("edit-website.no_images_modal")}</p>
                </div>
              ) : (
                <div style={styles.mediaGrid}>
                  {media.map((img) => (
                    <div key={img.id} style={{ ...styles.mediaItem, cursor: "pointer" }} onClick={() => selectImage(img.url)}>
                      <img src={img.url} alt="" style={styles.mediaThumbnail} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </>
  );
}

const styles = {
  topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e5e5e5", padding: "20px", position: "sticky", top: 0, zIndex: 100, background: "var(--glass)", backdropFilter: "blur(10px)", webkitBackdropFilter: "blur(10px)" },
  logo: { fontSize: "26px", fontWeight: "800", color: "var(--secondary-red)", letterSpacing: "-1px", textDecoration: "none" },
  divider: { width: 1, height: 18, background: "#ddd", display: "inline-block" },
  btnPreview: { background: "transparent", border: "1px solid #ddd", borderRadius: 8, padding: "6px 12px", fontSize: 13, color: "#555", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 },
  btnEdit: { background: "#f3f0ff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, color: "#7C3AED", textDecoration: "none", display: "flex", alignItems: "center", gap: 5 },
  container: { maxWidth: 900, margin: "0 auto", padding: "24px 16px" },
  sectionBox: { background: "white", borderRadius: 12, padding: 16, marginBottom: 24, border: "1px solid #eee" },
  sectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  btnAddCat: { background: "#f3f0ff", color: "#7C3AED", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontWeight: 600 },
  catInputRow: { display: "flex", gap: 6, alignItems: "center", marginBottom: 12 },
  catChips: { display: "flex", flexWrap: "wrap", gap: 8 },
  catChip: { display: "flex", alignItems: "center", gap: 4, background: "#f3f0ff", borderRadius: 20, padding: "5px 10px", border: "1px solid #e0d5ff" },
  catChipLabel: { fontSize: 13, color: "#5b21b6", fontWeight: 500 },
  catChipBtn: { background: "transparent", border: "none", cursor: "pointer", color: "#7C3AED", padding: "0 2px", fontSize: 12, display: "flex", alignItems: "center" },
  btnConfirm: { background: "#7C3AED", color: "white", border: "none", borderRadius: 7, padding: "6px 10px", cursor: "pointer", fontSize: 14 },
  btnCancelCat: { background: "#f0f0f0", color: "#555", border: "none", borderRadius: 7, padding: "6px 10px", cursor: "pointer", fontSize: 14 },
  btnAdd: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  emptyState: { textAlign: "center", padding: "60px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  productsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 },
  productCard: { background: "white", borderRadius: 12, overflow: "hidden", border: "1px solid #eee", display: "flex", flexDirection: "column" },
  productImageWrap: { position: "relative", height: 180 },
  productImage: { width: "100%", height: "100%", objectFit: "cover" },
  productImagePlaceholder: { width: "100%", height: "100%", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" },
  activeBadge: { position: "absolute", top: 8, right: 8, color: "white", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20 },
  categoryBadge: { position: "absolute", bottom: 8, left: 8, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, backdropFilter: "blur(4px)" },
  productInfo: { padding: "12px 14px", flex: 1 },
  productName: { fontWeight: 600, fontSize: 14, margin: "0 0 4px" },
  productPrice: { color: "#7C3AED", fontWeight: 700, fontSize: 15, margin: "0 0 6px" },
  productDesc: { color: "#888", fontSize: 12, margin: 0 },
  productActions: { display: "flex", gap: 6, padding: "10px 14px", borderTop: "1px solid #f0f0f0" },
  btnEditProduct: { flex: 1, background: "#f3f0ff", color: "#7C3AED", border: "none", borderRadius: 7, padding: "6px 0", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 },
  btnViewProduct: { flex: 1, background: "#f0fdf4", color: "#16a34a", border: "none", borderRadius: 7, padding: "6px 0", fontSize: 12, cursor: "pointer", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 },
  btnDeleteProduct: { background: "#fff0f0", color: "#e11d48", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 12, cursor: "pointer" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  modalBox: { background: "white", borderRadius: 12, width: "92%", maxWidth: 560, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #eee", flexShrink: 0 },
  modalBody: { padding: 16, overflowY: "auto", flex: 1, overflowX: "hidden" },
  modalFooter: { display: "flex", gap: 8, justifyContent: "flex-end", padding: "12px 16px", borderTop: "1px solid #eee", flexShrink: 0 },
  btnClose: { background: "transparent", border: "none", fontSize: 16, cursor: "pointer", color: "#555" },
  btnCancel: { background: "transparent", border: "1px solid #ddd", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" },
  btnSave: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 },
  btnUpload: { background: "#7C3AED", color: "white", border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 },
  btnPick: { background: "#7C3AED", color: "white", border: "none", borderRadius: 8, padding: "0 12px", fontSize: 16, cursor: "pointer", height: 42, flexShrink: 0 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 },
  fieldLabel: { fontSize: 12, color: "#888", fontWeight: 500 },
  input: { background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: 8, padding: "8px 12px", fontSize: 16, color: "#1a1a1a", width: "100%" },
  textarea: { background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: 8, padding: "8px 12px", fontSize: 16, color: "#1a1a1a", width: "100%", resize: "vertical", fontFamily: "inherit" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  imgPreview: { width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 8, border: "1px solid #eee", marginBottom: 6 },
  toggleBig: { width: 46, height: 24, borderRadius: 12, position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 },
  toggleDotBig: { width: 20, height: 20, background: "white", borderRadius: "50%", position: "absolute", top: 2, transition: "left 0.2s" },
  mediaGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },
  mediaItem: { borderRadius: 8, overflow: "hidden", aspectRatio: "1", background: "#f0f0f0" },
  mediaThumbnail: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
};